import React from 'react';
import { Control } from 'react-hook-form';

import {
  ErrorMessage,
  FormID,
  IsLoading,
  RegisterFormFieldType
} from '../../../../../types';
import { RevisionTaskFormData } from './RevisionTaskForm.types';
import { TaskRevisionFields } from '../../../tasksTypes';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';

import { Form } from '../../../../../helpers/Form';
import { InputField } from '../../../../../helpers/FormFields/InputField';

import { RevisionTaskStatusButtonsField } from './components/RevisionTaskStatusButtonsField';

import { TextAreaField } from '../../../../../helpers/FormFields/TextAreaField';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { Translate } from '../../../../../helpers/Translate';

import { TasksPermissions } from '../../../tasksConstants';

import { formsFields, statusesKeys } from '../../../../../locales/keys';

interface RevisionTaskFormProps {
  control: Control<RevisionTaskFormData>;
  form: FormID;
  isLoading: IsLoading;
  registerImplementationDate: RegisterFormFieldType<HTMLInputElement>;
  registerFeedback: RegisterFormFieldType<HTMLTextAreaElement>;
  implementationDateValidationError: ErrorMessage;
  isPlannedStartDateVisible: boolean;
  registerPlannedStartDate: RegisterFormFieldType<HTMLInputElement>;
  plannedStartDateValidationError: ErrorMessage;
}

function RevisionTaskForm({
  form,
  isLoading,
  registerImplementationDate,
  registerFeedback,
  implementationDateValidationError,
  control,
  isPlannedStartDateVisible,
  registerPlannedStartDate,
  plannedStartDateValidationError
}: RevisionTaskFormProps) {
  const currentUser = useCurrentUser();
  return (
    <Form className="flex-1 overflow-y-auto px-2" id={form}>
      <CheckPermissions
        action={TasksPermissions.READ_TASK_REVISION_INITIAL_STATUS}
      >
        <div className="p-4 flex gap-2 flex-col">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Translate id={statusesKeys.initialStatus} />
          </div>

          <div className="flex flex-col gap-3">
            <div data-toggle-item="-filter-content">
              <RevisionTaskStatusButtonsField
                name={TaskRevisionFields.STATUS}
                control={control}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </CheckPermissions>

      <CheckPermissions
        action={TasksPermissions.READ_TASK_REVISION_IMPLEMENTATION_DATE_FIELD}
      >
        <div className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex gap-3 flex-wrap">
              <div className="w-full sm:w-auto sm:flex-1 flex flex-wrap gap-2">
                <div className="flex-1">
                  <InputField
                    disabled={
                      isLoading ||
                      !currentUser.hasPermissions(
                        TasksPermissions.CHANGE_TASK_REVISION_IMPLEMENTATION_DATE_FILED
                      )
                    }
                    error={implementationDateValidationError}
                    i18nLabel={formsFields.implementationDate}
                    inputWrapperClassName="relative mt-1"
                    labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    name={registerImplementationDate.name}
                    onChange={registerImplementationDate.onChange}
                    ref={registerImplementationDate.ref}
                    showErrorIcon={false}
                    type="datetime-local"
                  />
                </div>

                {isPlannedStartDateVisible && (
                  <div className="flex-1">
                    <InputField
                      disabled={isLoading}
                      error={plannedStartDateValidationError}
                      i18nLabel={formsFields.plannedStartDate}
                      inputWrapperClassName="relative mt-1"
                      labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      name={registerPlannedStartDate.name}
                      onChange={registerPlannedStartDate.onChange}
                      ref={registerPlannedStartDate.ref}
                      showErrorIcon={false}
                      type="datetime-local"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CheckPermissions>
      <div className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 flex-wrap">
            <div className="w-full sm:w-auto sm:flex-1">
              <TextAreaField
                disabled={isLoading}
                i18nLabel={formsFields.description}
                labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                textAreaWrapperClassName="mt-1"
                name={registerFeedback.name}
                onChange={registerFeedback.onChange}
                ref={registerFeedback.ref}
                rows={5}
              />
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
}

export default RevisionTaskForm;
